/**
 * Build a Firestore-safe message payload
 * Enforces strict contract for all message writes
 * @param {Object} input - Raw message input
 * @returns {Object} - Firestore-safe message payload
 */
import { sanitizeAttachments } from './sanitizeAttachments';

export function buildMessagePayload(input) {
    // Validate required fields
    if (!input.text || typeof input.text !== 'string') {
        throw new Error('Message text is required and must be a string');
    }
    if (!input.channel || typeof input.channel !== 'string') {
        throw new Error('Message channel is required and must be a string');
    }

    // Sanitize attachments and enforce contract
    const safeAttachments = sanitizeAttachments(input.attachments || []);

    // Validate attachments array
    if (!Array.isArray(safeAttachments)) {
        throw new Error('buildMessagePayload: attachments must be an array');
    }

    // Check for any undefined values in attachments (deep)
    const hasUndefined = safeAttachments.some(att =>
        att === undefined ||
        (typeof att === 'object' && att !== null && Object.values(att).some(val => val === undefined))
    );
    if (hasUndefined) {
        throw new Error('buildMessagePayload: attachments contains undefined values');
    }

    // Build the payload with strict contract
    const payload = {
        text: input.text,
        timestamp: input.timestamp || new Date().toISOString(),
        isSender: input.isSender !== undefined ? input.isSender : true,
        channel: input.channel,
        metadata: input.metadata || {},
        // ALWAYS include attachments array (may be empty) to satisfy strict schema
        attachments: safeAttachments,
    };

    // Add optional fields
    if (input.subject) {
        payload.metadata.subject = input.subject;
    }
    if (input.isBroadcast) {
        payload.isBroadcast = true;
        payload.source = 'broadcast';
    }
    if (input.broadcastId) {
        payload.broadcastId = input.broadcastId;
    }
    if (input.status) {
        payload.status = input.status;
    }
    // Tag manual messages with source if not already set by broadcast/automation
    if (!payload.source) {
        payload.source = input.source || 'manual';
    }

    return payload;
}